import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Package,
    Settings,
    FileText,
    Camera,
    Upload,
    Save,
    X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { User } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const RecordRepair: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [asset, setAsset] = useState<Asset | null>(null);
    const [approvers, setApprovers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        partsRepaired: '',
        description: '',
        vendor: '',
        repairDate: new Date().toISOString().split('T')[0],
        cost: '',
        approvedBy: ''
    });

    const [files, setFiles] = useState<{ images: File[] }>({
        images: []
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [assetRes, approversRes] = await Promise.all([
                    api.get<Asset>(`/api/assets/${id}`),
                    api.get<{ users: User[] }>('/api/assets/repair-approvers')
                ]);
                setAsset(assetRes);
                // Fallback in case endpoint returns array or object with users/approvers
                if (Array.isArray(approversRes)) {
                    setApprovers(approversRes);
                } else if (approversRes && approversRes.users) {
                    setApprovers(approversRes.users);
                } else if (approversRes && (approversRes as any).approvers) {
                    setApprovers((approversRes as any).approvers);
                } else {
                    setApprovers([]);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load asset details or approvers');
                navigate(`/assets/${id}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, navigate]);

    if (!ability.can(ACTIONS.CREATE, RESOURCES.ASSET_REPAIR)) {
        return <AccessRestricted />;
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            
            const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'pdf'];
            const invalidFiles = newFiles.filter(file => {
                const ext = file.name.split('.').pop()?.toLowerCase();
                return !ext || !allowedExtensions.includes(ext);
            });
            
            if (invalidFiles.length > 0) {
                const firstInvalid = invalidFiles[0];
                const ext = firstInvalid.name.split('.').pop()?.toLowerCase() || 'unknown';
                toast.error(`File extension '.${ext}' is not allowed for file '${firstInvalid.name}'.`);
                // clear the input so the user can re-select
                e.target.value = '';
                return;
            }

            // check max 5 images total
            if (files.images.length + newFiles.length > 5) {
                toast.error('Maximum of 5 images allowed');
                return;
            }

            setFiles(prev => ({
                ...prev,
                images: [...prev.images, ...newFiles]
            }));
        }
    };

    const removeImage = (indexToRemove: number) => {
        setFiles(prev => ({
            ...prev,
            images: prev.images.filter((_, index) => index !== indexToRemove)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.partsRepaired) {
            toast.error('Please enter Parts Repaired');
            return;
        }
        if (!formData.description) {
            toast.error('Please enter a Description of Repair');
            return;
        }
        if (!formData.vendor) {
            toast.error('Please enter a Vendor');
            return;
        }
        if (!formData.repairDate) {
            toast.error('Please enter a Repair Date');
            return;
        }
        if (!formData.cost) {
            toast.error('Please enter Repair Cost');
            return;
        }
        if (!formData.approvedBy) {
            toast.error('Please select an Approver');
            return;
        }

        if (files.images.length === 0) {
            toast.error('Please upload at least one repair document or photo');
            return;
        }

        try {
            setSubmitting(true);
            const data = new FormData();

            Object.entries(formData).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    data.append(key, value);
                }
            });

            files.images.forEach(file => {
                data.append('attachments', file);
            });

            await api.post(`/api/assets/${id}/repair`, data, { skipErrorToast: true });

            toast.success('Repair record created successfully');
            navigate(`/assets/${id}`);
        } catch (error: any) {
            console.error('Error saving repair record:', error);
            toast.error(error.message || 'Failed to record repair');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !asset) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-8">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 flex-shrink-0"
                    onClick={() => navigate(`/assets/${id}`)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Record Asset Repair</h1>
                    <p className="text-gray-500 text-sm font-medium mt-1">
                        Create repair record for {asset.name} ({asset.tag})
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col xl:flex-row gap-6">
                {/* Main Content Area */}
                <div className="flex-1 space-y-6">
                    {/* Asset Information block */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <h2 className="text-base font-bold text-gray-900 mb-6 font-poppins">Asset Information</h2>
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100 shadow-sm shrink-0 overflow-hidden">
                                {asset.image ? (
                                    <img src={asset.image} alt={asset.name} className="w-full h-full object-cover" />
                                ) : (
                                    <Package size={24} />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 leading-tight">{asset.name}</h3>
                                <div className="text-sm font-medium text-gray-500 mt-1 mb-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                    <span>{asset.tag}</span>
                                    <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-gray-300"></span>
                                    <span>{asset.type}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="px-2.5 py-0.5 rounded-lg border-gray-100 text-[10px] font-bold uppercase tracking-wider bg-gray-50">
                                        {asset.status}
                                    </Badge>
                                    <span className="text-xs font-medium text-gray-500">{asset.brand} {asset.model}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Repair Details */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <Settings className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Repair Details</h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Provide details about the repair work performed</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="text-xs font-bold text-gray-900">Parts Repaired <span className="text-red-500">*</span></label>
                                <Input
                                    name="partsRepaired"
                                    placeholder="e.g., Brake pads, Engine oil, Screen assembly"
                                    value={formData.partsRepaired}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>

                            <div className="col-span-1 md:col-span-2 space-y-2">
                                <label className="text-xs font-bold text-gray-900">Description of Repair <span className="text-red-500">*</span></label>
                                <Textarea
                                    name="description"
                                    placeholder="Provide detailed description of the repair work, including the problem that was fixed and any testing performed.."
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[100px] p-4 resize-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Vendor <span className="text-red-500">*</span></label>
                                <Input
                                    name="vendor"
                                    placeholder="e.g. AutoTech Repairs, TechSupport Solutions"
                                    value={formData.vendor}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Repair Date <span className="text-red-500">*</span></label>
                                <Input
                                    type="date"
                                    name="repairDate"
                                    max="9999-12-31"
                                    value={formData.repairDate}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 px-4"
                                    required
                                />
                            </div>

                            <div className="space-y-2 relative">
                                <label className="text-xs font-bold text-gray-900">Repair Cost (excluding taxes) <span className="text-red-500">*</span></label>
                                <div className="absolute left-4 top-[38px] text-gray-500 font-medium">$</div>
                                <Input
                                    name="cost"
                                    type="number"
                                    placeholder="0.00"
                                    value={formData.cost}
                                    onChange={handleChange}
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 pl-8 pr-4"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-900">Approved By <span className="text-red-500">*</span></label>
                                <Select value={formData.approvedBy} onValueChange={(val) => handleSelectChange('approvedBy', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12">
                                        <SelectValue placeholder="Select approver..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100 max-h-[300px]">
                                        {approvers.map(user => (
                                            <SelectItem key={user.id} value={user.id.toString()} className="py-2.5">
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-gray-900">{user.name}</span>
                                                    <span className="text-[10px] text-gray-500">{user.jobRole || 'No role'}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Repair Images */}
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <Camera className="text-gray-400" size={20} />
                            <div>
                                <h2 className="text-base font-bold text-gray-900 font-poppins">Repair Documents <span className="text-red-500">*</span></h2>
                                <p className="text-xs font-medium text-gray-400 mt-0.5">Upload photos or PDF documents of the repair work (required, max 5 files)</p>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <label className="text-sm font-bold text-gray-900 flex items-center justify-between">
                                    <span>Upload Files</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/jpeg,image/png,image/gif,application/pdf"
                                        onChange={(e) => handleFileChange(e)}
                                        className="hidden"
                                        id="image-upload"
                                    />
                                    <label
                                        htmlFor="image-upload"
                                        className="flex items-center w-full min-h-[48px] px-4 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-gray-100 transition-colors cursor-pointer group justify-between"
                                    >
                                        <div className="flex items-center">
                                            <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-100 text-xs font-bold text-gray-700 mr-3 shadow-sm group-hover:shadow transition-shadow">
                                                Choose Files
                                            </div>
                                            <span className="text-sm font-medium text-gray-500 truncate">
                                                {files.images.length > 0 ? `${files.images.length} file(s) chosen` : 'No file chosen'}
                                            </span>
                                        </div>
                                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center shadow-sm">
                                            <Upload size={14} className="text-gray-500" />
                                        </div>
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">Supported formats: JPG, PNG, GIF, PDF. Max file size: 5MB each.</p>
                                {/* Preview Images */}
                                {files.images.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {files.images.map((file, i) => (
                                            <div key={i} className="flex items-center gap-2 bg-gray-50 pl-3 pr-2 py-1.5 rounded-lg border border-gray-100">
                                                <Camera size={14} className="text-blue-500" />
                                                <span className="text-xs font-medium text-gray-700 truncate max-w-[150px]">{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(i)}
                                                    className="p-1 hover:bg-gray-200 rounded-md transition-colors text-gray-400 hover:text-red-500 ml-1"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Sticky Area */}
                <div className="w-full xl:w-[350px] flex-shrink-0">
                    <div className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm sticky top-6">
                        <div className="flex items-center gap-3 mb-8">
                            <Settings className="text-gray-400" size={20} />
                            <h2 className="text-base font-bold text-gray-900 font-poppins">Repair Summary</h2>
                        </div>

                        <div className="space-y-6 mb-8">
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Asset</span>
                                <p className="text-sm font-medium text-gray-900">{asset.name} ({asset.tag})</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Parts Repaired</span>
                                <p className="text-sm font-medium text-gray-900">{formData.partsRepaired || 'Not specified'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Vendor</span>
                                <p className="text-sm font-medium text-gray-900 md:truncate">{formData.vendor || 'Not specified'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Repair Cost</span>
                                <p className="text-sm font-medium text-gray-900">{formData.cost ? `$${Number(formData.cost).toFixed(2)}` : 'Not set'}</p>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Documents</span>
                                <p className="text-sm font-medium text-gray-900">{files.images.length} uploaded</p>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full rounded-2xl bg-slate-900 hover:bg-slate-800 text-white h-12 font-bold mb-6 gap-2 text-sm shadow-md transition-all"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <Save size={18} />
                                    Save Repair Record
                                </>
                            )}
                        </Button>

                        <div className="space-y-3">
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Record will be saved to asset history</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Activity will be logged</span>
                            </div>
                            <div className="flex items-start gap-2 text-xs font-medium text-gray-500">
                                <span className="text-gray-300 mt-0.5">•</span>
                                <span>Approver will be notified</span>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default RecordRepair;
